import React from 'react';
import { useNavigate } from 'react-router-dom';
import VistaCalcLogo from './VistaCalcLogo';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const calculators = [
    {
      id: 'mortgage',
      title: 'Mortgage Calculator',
      description: 'Calculate mortgage payments, affordability, and amortization schedules',
      icon: '🏠',
      color: 'from-vista-silver-500 to-vista-silver-600',
      path: '/mortgage'
    },
    {
      id: 'investment',
      title: 'Investment Calculator',
      description: 'Analyze ROI, cash flow, and investment performance metrics',
      icon: '📈',
      color: 'from-vista-teal-400 to-vista-teal-500',
      path: '/investment'
    },
    {
      id: 'portfolio',
      title: 'Portfolio Manager',
      description: 'Manage multiple properties and analyze portfolio-wide cashflow',
      icon: '🏢',
      color: 'from-vista-silver-600 to-vista-silver-700',
      path: '/portfolio'
    },
    {
      id: 'monte-carlo',
      title: 'Monte Carlo Simulation',
      description: 'Advanced risk analysis and scenario planning with Monte Carlo simulation',
      icon: '🎲',
      color: 'from-purple-500 to-purple-600',
      path: '/monte-carlo'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-vista-teal-50 via-vista-silver-50 to-vista-teal-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-vista-silver-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-center items-center">
            <VistaCalcLogo size="lg" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Hero Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <h1 className="text-4xl lg:text-5xl font-bold text-vista-silver-900 leading-tight">
                Professional Property Investment
                <span className="text-vista-teal-600 block">Calculators</span>
              </h1>
              
              <p className="text-xl text-vista-silver-700 leading-relaxed">
                Comprehensive financial modeling tools for French property investment. 
                Calculate taxes, mortgages, and investment returns with precision.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-vista-teal-100 rounded-full flex items-center justify-center">
                  <span className="text-vista-teal-600 font-semibold">✓</span>
                </div>
                <span className="text-vista-silver-800">France-UK Tax Calculator</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-vista-teal-100 rounded-full flex items-center justify-center">
                  <span className="text-vista-teal-600 font-semibold">✓</span>
                </div>
                <span className="text-vista-silver-800">Mortgage & Affordability Analysis</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-vista-teal-100 rounded-full flex items-center justify-center">
                  <span className="text-vista-teal-600 font-semibold">✓</span>
                </div>
                <span className="text-vista-silver-800">Investment ROI & Cash Flow Modeling</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-vista-teal-100 rounded-full flex items-center justify-center">
                  <span className="text-vista-teal-600 font-semibold">✓</span>
                </div>
                <span className="text-vista-silver-800">Professional-Grade Accuracy</span>
              </div>
            </div>

            <div className="bg-vista-teal-50 border border-vista-teal-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-vista-teal-900 mb-2">
                Trusted by Property Professionals
              </h3>
              <p className="text-vista-teal-800">
                Built with real-world tax regulations and financial modeling best practices. 
                Get the insights you need for informed investment decisions.
              </p>
            </div>
          </div>

          {/* Right Column - Calculator Access */}
          <div className="bg-white rounded-2xl shadow-xl border border-vista-silver-200 p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-vista-silver-900 mb-2">
                Access Your Calculators
              </h2>
              <p className="text-vista-silver-600">
                Choose from our professional property investment tools
              </p>
            </div>

            {/* Calculators Grid */}
            <div className="space-y-4">
              {calculators.map((calculator) => (
                <div
                  key={calculator.id}
                  className="bg-vista-silver-50 rounded-xl p-4 hover:bg-vista-silver-100 transition-colors cursor-pointer group border border-vista-silver-200"
                  onClick={() => navigate(calculator.path)}
                >
                  <div className="flex items-center space-x-4">
                    <div className="text-3xl">{calculator.icon}</div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-vista-silver-900 group-hover:text-vista-teal-600 transition-colors">
                        {calculator.title}
                      </h3>
                      <p className="text-sm text-vista-silver-600">
                        {calculator.description}
                      </p>
                    </div>
                    <div className={`bg-gradient-to-r ${calculator.color} text-white px-3 py-1 rounded-lg text-xs font-medium group-hover:scale-105 transition-transform`}>
                      Open →
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 text-center">
              <p className="text-xs text-vista-silver-500">
                All calculators are free to use. No account required.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats Section */}
        <div className="mt-16">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl shadow-lg border border-vista-silver-200 p-6 text-center">
              <div className="w-12 h-12 bg-vista-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-vista-teal-600 text-xl">🔧</span>
              </div>
              <h3 className="text-lg font-semibold text-vista-silver-900 mb-2">Professional Tools</h3>
              <p className="text-vista-silver-600">4 specialized calculators for property investment</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-vista-silver-200 p-6 text-center">
              <div className="w-12 h-12 bg-vista-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-vista-teal-600 text-xl">🎯</span>
              </div>
              <h3 className="text-lg font-semibold text-vista-silver-900 mb-2">Market Focused</h3>
              <p className="text-vista-silver-600">Built specifically for the French property market</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-vista-silver-200 p-6 text-center">
              <div className="w-12 h-12 bg-vista-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-vista-teal-600 text-xl">📊</span>
              </div>
              <h3 className="text-lg font-semibold text-vista-silver-900 mb-2">Real-Time Results</h3>
              <p className="text-vista-silver-600">Instant calculations with professional accuracy</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white/80 backdrop-blur-sm border-t border-vista-silver-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-vista-silver-600">
            <p>&copy; 2024 VistaCalc. All rights reserved.</p>
            <p className="mt-2 text-sm">
              Professional property investment calculators for the French market
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
